
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { KnowledgeCollection } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { slugify } from "@/lib/utils";
import { 
  Book, BookOpen, BookMarked, FileText, 
  HelpCircle, Info, Settings, MessageCircle, 
  FileQuestion, Video, Lightbulb, Rocket, 
  Compass, ChevronDown, Star, Database,
  Code, Shield, Target, Award, Zap, 
  Bookmark, Briefcase, ClipboardCheck, Cpu,
  FileCode, Fingerprint, Flag, Gift, 
  GraduationCap, HeartHandshake, Laptop, Lock,
  MapPin, PenTool, Users, Wrench
} from "lucide-react";

const ICON_OPTIONS = [
  { name: "Book", icon: Book },
  { name: "Book Open", icon: BookOpen },
  { name: "Book Marked", icon: BookMarked },
  { name: "File", icon: FileText },
  { name: "Help", icon: HelpCircle },
  { name: "Info", icon: Info },
  { name: "Settings", icon: Settings },
  { name: "Message", icon: MessageCircle },
  { name: "FAQ", icon: FileQuestion },
  { name: "Video", icon: Video },
  { name: "Lightbulb", icon: Lightbulb },
  { name: "Rocket", icon: Rocket },
  { name: "Compass", icon: Compass },
  { name: "Star", icon: Star },
  { name: "Database", icon: Database },
  { name: "Code", icon: Code },
  { name: "Shield", icon: Shield },
  { name: "Target", icon: Target },
  { name: "Award", icon: Award },
  { name: "Zap", icon: Zap },
  { name: "Bookmark", icon: Bookmark },
  { name: "Briefcase", icon: Briefcase },
  { name: "Checklist", icon: ClipboardCheck },
  { name: "Computer", icon: Cpu },
  { name: "File Code", icon: FileCode },
  { name: "Security", icon: Fingerprint },
  { name: "Flag", icon: Flag },
  { name: "Gift", icon: Gift },
  { name: "Education", icon: GraduationCap },
  { name: "Support", icon: HeartHandshake },
  { name: "Laptop", icon: Laptop },
  { name: "Lock", icon: Lock },
  { name: "Location", icon: MapPin },
  { name: "Design", icon: PenTool },
  { name: "Team", icon: Users },
  { name: "Tools", icon: Wrench },
];

type KnowledgeCollectionFormProps = {
  companyId: string;
  collection?: KnowledgeCollection;
  onCancel: () => void;
  onSave: () => void;
};

const KnowledgeCollectionForm = ({
  companyId,
  collection,
  onCancel,
  onSave,
}: KnowledgeCollectionFormProps) => {
  const [name, setName] = useState(collection?.name || "");
  const [description, setDescription] = useState(collection?.description || "");
  const [slug, setSlug] = useState(collection?.slug || "");
  const [isPublished, setIsPublished] = useState(
    collection?.is_published !== undefined ? collection.is_published : true
  );
  const [iconName, setIconName] = useState(collection?.icon || "Book");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleNameChange = (value: string) => {
    setName(value);
    if (!collection) {
      // Only auto-generate slug for new collections
      setSlug(slugify(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Collection name is required",
        variant: "destructive",
      });
      return;
    }

    if (!slug.trim()) {
      toast({
        title: "Error",
        description: "Collection slug is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: user } = await supabase.auth.getUser();
      
      if (collection) {
        // Update existing collection
        const { error } = await supabase
          .from("knowledge_collections")
          .update({
            name,
            description,
            slug,
            icon: iconName,
            is_published: isPublished,
            updated_at: new Date().toISOString(),
          })
          .eq("id", collection.id);

        if (error) {
          console.error("Error updating collection:", error);
          toast({
            title: "Error",
            description: error.message || "Failed to update collection",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Success",
          description: "Collection updated successfully",
        });
      } else {
        // Create new collection
        const { error } = await supabase.from("knowledge_collections").insert({
          company_id: companyId,
          name,
          description,
          slug,
          icon: iconName,
          is_published: isPublished,
          created_by: user.user?.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        if (error) {
          console.error("Error creating collection:", error);
          toast({
            title: "Error",
            description: error.message || "Failed to create collection",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Success",
          description: "Collection created successfully",
        });
      }

      onSave();
    } catch (error) {
      console.error("Unexpected error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Find the current icon component
  const SelectedIcon = ICON_OPTIONS.find(option => option.name === iconName)?.icon || Book;

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>
            {collection ? "Edit Collection" : "Create Collection"}
          </DialogTitle>
          <DialogDescription>
            {collection
              ? "Update your knowledge base collection details."
              : "Create a new knowledge base collection to organize your articles."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Collection Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., Getting Started"
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="slug">
                Slug <span className="text-xs text-muted-foreground">(URL-friendly name)</span>
              </Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(slugify(e.target.value))}
                placeholder="e.g., getting-started"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="icon">Icon</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between"
                    type="button"
                  >
                    <div className="flex items-center">
                      <SelectedIcon className="h-4 w-4 mr-2" />
                      <span>{iconName}</span>
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 max-h-[300px] overflow-y-auto">
                  {ICON_OPTIONS.map((option) => (
                    <DropdownMenuItem 
                      key={option.name}
                      onClick={() => setIconName(option.name)}
                      className="flex items-center cursor-pointer"
                    >
                      <option.icon className="h-4 w-4 mr-2" />
                      <span>{option.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe what this collection contains"
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="published" className="cursor-pointer">
                Published
              </Label>
              <Switch
                id="published"
                checked={isPublished}
                onCheckedChange={setIsPublished}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? collection
                  ? "Updating..."
                  : "Creating..."
                : collection
                ? "Update Collection"
                : "Create Collection"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeCollectionForm;
